import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { LogOut, Menu, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { NavLink, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { api, LOGIN_PATH, type Bot, type Me } from "@/lib/api";
import { signOut } from "@/lib/auth-client";
import { cn } from "@/lib/utils";
import { allows, type Module, type ModuleLevel } from "@kickwin/shared";
import { ErrorNote, Loading, Select } from "./primitives";

/**
 * The shell (spec §12 Pages).
 *
 * A fixed rail on a desktop, where the console is left open all day and
 * hiding navigation behind a click costs more than the 232px it saves. Below
 * lg (phones, folding phones, tablets) those 232px are most of the screen, so
 * the same rail slides over from a menu button instead. Every page is scoped
 * to one bot, so the bot switcher sits above the nav — it changes what
 * everything below means.
 */

interface NavItem {
  to: string;
  label: string;
  /** Shown only to someone whose role allows at least `level` here (decision 42). */
  module: Module;
  level: ModuleLevel;
}

/** Grouped by what an operator is doing, not by what the data is. */
const BOT_SECTIONS: { heading: string | null; items: NavItem[] }[] = [
  {
    heading: null,
    items: [
      { to: "", label: "Overview", module: "overview", level: "view" },
      { to: "players", label: "Players", module: "players", level: "view" },
      { to: "sources", label: "Sources", module: "sources", level: "view" },
      { to: "broadcasts", label: "Broadcasts", module: "broadcasts", level: "view" },
    ],
  },
  {
    heading: "Automation",
    items: [
      { to: "auto-replies", label: "Auto reply", module: "autoReplies", level: "view" },
      { to: "commands", label: "Commands", module: "commands", level: "view" },
      { to: "footer", label: "Bottom menu", module: "footer", level: "view" },
      { to: "languages", label: "Languages", module: "commands", level: "view" },
    ],
  },
  {
    heading: "Setup",
    items: [
      { to: "groups", label: "CS group", module: "groups", level: "view" },
      { to: "import", label: "Import", module: "import", level: "use" },
    ],
  },
  {
    heading: "Logs",
    items: [
      { to: "events", label: "System log", module: "systemLog", level: "view" },
      { to: "audit", label: "Audit log", module: "auditLog", level: "view" },
    ],
  },
];

const ACCOUNT_PAGES: NavItem[] = [
  { to: "/bots", label: "Bots", module: "bots", level: "view" },
  { to: "/admins", label: "Admins", module: "admins", level: "view" },
  { to: "/roles", label: "Roles", module: "admins", level: "view" },
];

/** The page's name for the top bar, from where the router is. */
function titleFor(pathname: string): string {
  if (pathname === "/bots") return "Bots";
  if (pathname === "/admins") return "Admins";
  if (pathname === "/roles") return "Roles";
  if (pathname === "/help") return "CS in Telegram";
  const section = /^\/bots\/[^/]+\/?([^/]*)(\/[^/]+)?/.exec(pathname);
  if (!section) return "KickWin CS";
  const [, page, detail] = section;
  if (page === "players" && detail) return "Player";
  const titles: Record<string, string> = {
    "": "Overview",
    players: "Players",
    sources: "Sources",
    broadcasts: "Broadcasts",
    "auto-replies": "Auto reply",
    commands: "Commands",
    footer: "Bottom menu",
    languages: "Languages",
    groups: "CS group",
    import: "Import",
    events: "System log",
    audit: "Audit log",
  };
  return titles[page ?? ""] ?? "KickWin CS";
}

export function Shell() {
  const navigate = useNavigate();
  const { botId } = useParams();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  const me = useQuery({ queryKey: ["me"], queryFn: () => api.get<Me>("/me") });
  const bots = useQuery({
    queryKey: ["bots"],
    queryFn: () => api.get<Bot[]>("/bots"),
    // An ordinary admin cannot list bots; the switcher falls back to the id
    // already in the URL.
    retry: false,
  });

  if (me.isLoading) return <Loading label="Signing in" />;

  if (me.error) {
    return (
      <div className="mx-auto max-w-xl px-6 py-20">
        <h1 className="mb-3 text-lg font-semibold">You cannot open the panel</h1>
        <ErrorNote error={me.error} />
        <p className="mt-3 text-muted-foreground">
          Signing in proves who you are; the admin list decides what you can do. If you have just
          been added, reload the page.
        </p>
      </div>
    );
  }

  const pageTitle = titleFor(location.pathname);
  const botList = bots.data ?? [];
  const currentBot = botList.find((b) => String(b.id) === botId);

  const rail = (
    <Rail
      me={me.data!}
      botId={botId}
      botList={botList}
      onBotChange={(id) => {
        setMenuOpen(false);
        navigate(`/bots/${id}`);
      }}
      onNavigate={() => setMenuOpen(false)}
    />
  );

  return (
    <div className="flex h-dvh">
      <nav className="hidden w-[248px] shrink-0 flex-col border-r border-border bg-rail text-rail-foreground lg:flex">
        {rail}
      </nav>

      <Dialog.Root open={menuOpen} onOpenChange={setMenuOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-black/45 lg:hidden" />
          <Dialog.Content
            aria-describedby={undefined}
            className="fixed inset-y-0 left-0 z-50 flex w-[min(280px,85vw)] flex-col border-r border-border bg-rail text-rail-foreground shadow-xl lg:hidden"
          >
            <Dialog.Title className="sr-only">Menu</Dialog.Title>
            <Dialog.Close
              aria-label="Close menu"
              className="absolute top-3 right-3 grid size-9 place-items-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
            >
              <X className="size-5" />
            </Dialog.Close>
            {rail}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="flex h-14 shrink-0 items-center gap-2 border-b border-border bg-card px-3 sm:gap-3 sm:px-6">
          <button
            type="button"
            aria-label="Open menu"
            onClick={() => setMenuOpen(true)}
            className="grid size-9 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground lg:hidden"
          >
            <Menu className="size-5" />
          </button>
          <h1 className="min-w-0 truncate text-base font-semibold">{pageTitle}</h1>
          {currentBot && (
            <span className="ml-auto hidden shrink-0 items-center gap-2 rounded-full border border-border bg-muted/50 px-3 py-1 text-muted-foreground md:inline-flex">
              <span className="size-1.5 rounded-full bg-ok" aria-hidden />
              <span className="font-medium text-foreground">@{currentBot.username}</span>
              <span className="mono text-xs">{currentBot.telegramBotId}</span>
            </span>
          )}
        </header>

        <main className="min-h-0 flex-1 overflow-y-auto">
          {/* Content keeps a readable width instead of stretching across a
              wide monitor. */}
          <div className="mx-auto w-full max-w-6xl p-3 sm:p-6 lg:p-8">
            <Outlet context={{ me: me.data!, bots: botList }} />
          </div>
        </main>
      </div>
    </div>
  );
}

/**
 * The rail's contents, rendered twice: fixed beside the page from lg up, and
 * inside a slide-over below that. Every link closes the slide-over, so a tap
 * on a phone lands on the page rather than leaving the menu covering it.
 */
function Rail({
  me,
  botId,
  botList,
  onBotChange,
  onNavigate,
}: {
  me: Me;
  botId: string | undefined;
  botList: Bot[];
  onBotChange: (id: string) => void;
  onNavigate: () => void;
}) {
  const role = me.role;
  const can = (item: NavItem) => allows(me.permissions, item.module, item.level as never);
  const linkClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      "block rounded-md px-3 py-2 font-medium transition-colors lg:py-1.5",
      isActive
        ? "bg-rail-accent text-primary"
        : "text-rail-foreground hover:bg-secondary hover:text-foreground",
    );

  return (
    <>
      <div className="flex items-center gap-2.5 px-4 pt-4 pb-3">
        <div className="grid size-8 place-items-center rounded-lg bg-primary text-sm font-bold text-primary-foreground">
          K
        </div>
        <div>
          <div className="leading-tight font-semibold text-foreground">KickWin CS</div>
          <div className="text-xs text-muted-foreground">Control plane</div>
        </div>
      </div>

      <div className="px-3 pb-3">
        <Select
          aria-label="Bot"
          className="w-full"
          value={botId ?? ""}
          onChange={(e) => onBotChange(e.target.value)}
        >
          {botList.length === 0 && <option value="">No bots yet</option>}
          {botList.map((bot) => (
            <option key={bot.id} value={bot.id}>
              @{bot.username}
              {bot.status === "disabled" ? " (disabled)" : ""}
            </option>
          ))}
        </Select>
      </div>

      <div className="flex-1 overflow-y-auto px-2 pb-4">
        {botId &&
          BOT_SECTIONS.map((section) => ({
            ...section,
            items: section.items.filter((item) => can(item)),
          }))
            .filter((section) => section.items.length > 0)
            .map((section) => (
              <div key={section.heading ?? "main"}>
                {section.heading && (
                  <div className="mt-5 mb-1.5 px-3 text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
                    {section.heading}
                  </div>
                )}
                <ul className="grid gap-0.5">
                  {section.items.map((item) => (
                    <li key={item.label}>
                      <NavLink
                        end={item.to === ""}
                        to={`/bots/${botId}/${item.to}`.replace(/\/$/, "")}
                        onClick={onNavigate}
                        className={linkClass}
                      >
                        {item.label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

        {ACCOUNT_PAGES.some(can) && (
          <>
            <div className="mt-5 mb-1.5 px-3 text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
              Account
            </div>
            <ul className="grid gap-0.5">
              {ACCOUNT_PAGES.filter(can).map((item) => (
                <li key={item.label}>
                  <NavLink end to={item.to} onClick={onNavigate} className={linkClass}>
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </>
        )}

        {/* Everyone signed in may read it: it describes Telegram, not data. */}
        <div className="mt-5 mb-1.5 px-3 text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
          Help
        </div>
        <ul className="grid gap-0.5">
          <li>
            <NavLink end to="/help" onClick={onNavigate} className={linkClass}>
              CS in Telegram
            </NavLink>
          </li>
        </ul>
      </div>

      <div className="flex items-center gap-2 border-t border-border px-4 py-3">
        <div className="min-w-0 flex-1">
          <div className="truncate" title={me.email}>
            {me.email}
          </div>
          <div className="text-xs text-muted-foreground">
            {role === "super_admin" ? "Super admin" : (me.roleName ?? "Team member")}
          </div>
        </div>
        <button
          type="button"
          onClick={async () => {
            // Ends the session server-side — the row is deleted, not just
            // forgotten here — then reloads onto the login page.
            await signOut();
            window.location.replace(LOGIN_PATH);
          }}
          aria-label="Log out"
          title="Log out"
          className="grid size-9 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
        >
          <LogOut className="size-4" />
        </button>
      </div>
    </>
  );
}
